/**
 * Status lifecycle PO (`products.status`).
 *
 * Status AKTIF yang masih ditulis sistem hanya 4: `OPEN PO` (default saat buat) →
 * `CLOSED PO` (tutup PO) → `PROSES PRODUKSI` (submit produksi) → `READY LENGKAP`
 * (barang datang / Tandai Ready). Tidak ada lagi pemilihan status manual — semua
 * transisi dijalankan otomatis oleh aksi.
 *
 * `GAGAL` (mig 3940) adalah status TERMINAL di luar rantai itu: ditulis otomatis
 * saat SELURUH pcs produk gagal produksi. Tak ada transisi keluar darinya.
 *
 * `PROSES INPUT`, `READY SEBAGIAN`, dan `PENDISTRIBUSIAN` PENSIUN sejak redesign
 * produksi (mig 079 menjadikan fase produksi biner PROSES PRODUKSI ↔ READY
 * LENGKAP); tak ada alur aktif yang menulisnya. Ketiganya SENGAJA dipertahankan
 * di tipe/daftar ini agar (1) produk lama yang masih ber-status itu tetap bisa
 * dirender (`getStatusTone`/label), dan (2) `READY SEBAGIAN` tetap valid sebagai
 * status orderable ready_stock (`ORDERABLE_READY_STOCK_STATUSES` + trigger DB
 * `enforce_orderable_product_on_order`). Jangan tawarkan sebagai pilihan input.
 */
import { isQuotaExhausted } from "./poQuota";

export type PreorderStatus =
  | "OPEN PO"
  | "CLOSED PO"
  | "PROSES INPUT"
  | "PROSES PRODUKSI"
  | "READY SEBAGIAN"
  | "READY LENGKAP"
  | "PENDISTRIBUSIAN"
  | "GAGAL";

export const preorderStatuses: PreorderStatus[] = [
  "OPEN PO",
  "CLOSED PO",
  "PROSES INPUT",
  "PROSES PRODUKSI",
  "READY SEBAGIAN",
  "READY LENGKAP",
  "PENDISTRIBUSIAN",
  "GAGAL"
];

/**
 * Status TERMINAL: batch produksi yang seluruh pcs-nya gagal (mig 3940/3960).
 * Tak ada transisi keluar — produk berhenti di sini, kartunya tinggal di kolom
 * Selesai board Produksi. Bukan pilihan input: hanya ditulis
 * `mark_variants_production_failed` saat gagal TOTAL.
 */
export function isFailedProductionStatus(
  status: PreorderStatus | string | undefined | null
): boolean {
  return typeof status === "string" && status.trim().toUpperCase() === "GAGAL";
}

/**
 * Status produk yang masih boleh dipesan pembeli — **bergantung tipe produk**.
 *
 * - Produk **ready stock** (`product_type='ready_stock'`, selalu dibuat dengan
 *   `status='READY LENGKAP'`): boleh dipesan saat `READY SEBAGIAN`/`READY LENGKAP`
 *   (stok riil; guard stok ada di trigger `set_ready_stock_keep`, mig 062).
 * - Produk **preorder** (`product_type='preorder'`): HANYA boleh dipesan saat
 *   `OPEN PO`. Begitu PO ditutup (`CLOSED PO`/`PROSES …`) ATAU barangnya sudah
 *   datang (`READY SEBAGIAN`/`READY LENGKAP`/`PENDISTRIBUSIAN`), stok yang ada
 *   sudah dialokasikan ke pemesan PO lama — reseller baru TIDAK boleh menumpang
 *   pesan (order baru akan nyangkut "Belum Ready" selamanya).
 *
 * Pemisahan ini penting karena preorder juga melewati `READY SEBAGIAN`/`READY
 * LENGKAP` di lifecycle-nya, jadi cek status saja (buta tipe) keliru menerima
 * pesanan baru pada preorder yang sudah Ready. Dipakai bersama oleh storefront
 * (UI), `createCheckoutOrder`, dan dicerminkan trigger DB
 * `enforce_orderable_product_on_order` (mig 067, diperbarui mig 090).
 */
export const ORDERABLE_PREORDER_STATUSES: PreorderStatus[] = ["OPEN PO"];
export const ORDERABLE_READY_STOCK_STATUSES: PreorderStatus[] = [
  "READY SEBAGIAN",
  "READY LENGKAP"
];

/**
 * Status produk yang menerima "Tambah Produk" ADMIN ke keranjang pelanggan:
 * orderable (OPEN PO/READY) + CLOSED PO (pesanan manual ikut batch produksi).
 * Satu daftar untuk pencarian Tambah Produk (`adminCart.ts`) DAN popup "Belum
 * ada di pesanan" Scan Tagihan (`invoiceScan.ts`) — keduanya membuka modal yang
 * sama. Gerbang otoritatifnya tetap di RPC (`admin_create_order` dkk.).
 */
export const ADMIN_ADDABLE_PRODUCT_STATUSES: readonly PreorderStatus[] = [
  "OPEN PO",
  "READY SEBAGIAN",
  "READY LENGKAP",
  "CLOSED PO"
];

export function isOrderableStatus(
  status: PreorderStatus | string | undefined | null,
  productType?: "preorder" | "ready_stock" | string | null
): boolean {
  if (productType === "ready_stock") {
    return ORDERABLE_READY_STOCK_STATUSES.includes(status as PreorderStatus);
  }
  // Default = preorder (mengikuti default mapPublicProduct/ProductForm).
  return ORDERABLE_PREORDER_STATUSES.includes(status as PreorderStatus);
}

/**
 * Produk yang stoknya ditrack per-varian (ready stock). Untuk produk seperti ini
 * UI varian boleh meng-grey-out pilihan yang stoknya habis. Produk preorder tidak
 * ditrack stoknya di storefront (dipesan sebelum barang ada) → semua pilihan aktif.
 */
export function isStockTracked(
  product: Pick<Product, "productType"> | undefined | null
): boolean {
  return product?.productType === "ready_stock";
}

/** Varian dianggap punya stok bila `stockQty` > 0. `undefined`/0 = habis. */
export function isVariationInStock(
  variation: Pick<ProductVariation, "stockQty"> | undefined | null
): boolean {
  return (variation?.stockQty ?? 0) > 0;
}

/** Varian in-stock pertama — dipakai sebagai pilihan default produk ready stock. */
export function firstInStockVariation(
  variations: ProductVariation[]
): ProductVariation | undefined {
  return variations.find((v) => isVariationInStock(v));
}

/**
 * Apakah ADA varian in-stock yang mengandung `optionId` SEKALIGUS semua
 * `otherSelectedOptionIds` (pilihan yang sedang aktif di grup lain). Dipakai untuk
 * meng-grey-out option yang—pada kombinasi yang sedang dipilih—stoknya habis,
 * ala Shopee (mis. ukuran "M" yang habis untuk warna "Merah" yang sedang dipilih).
 */
export function hasInStockVariationForOption(
  variations: ProductVariation[],
  optionId: string,
  otherSelectedOptionIds: string[] = []
): boolean {
  return hasEligibleVariationForOption(true, variations, optionId, otherSelectedOptionIds);
}

/**
 * Varian ini masih bisa dipesan? SATU sumbu untuk kedua tipe produk, dalam
 * bentuk yang dipegang SELEKTOR varian (`stockTracked` + `quota`) alih-alih
 * objek produk — `isVariationAvailable` di bawah adalah muka yang sama untuk
 * pemanggil yang memegang `Product`.
 *
 * Keduanya sengaja satu definisi: sebelum ini `VariantGroupSelector` menyimpan
 * salinan aturannya sendiri di dalam `handleClick` (`isEligible`), sehingga
 * "boleh dipesan" punya dua tempat yang bisa menyimpang diam-diam.
 */
export function isVariationEligible(
  stockTracked: boolean,
  variation: Pick<ProductVariation, "id" | "stockQty" | "poQuota">,
  quota?: PoQuotaContext
): boolean {
  if (stockTracked) return isVariationInStock(variation);
  return !isQuotaExhausted(
    quota?.limited ?? false,
    variation.poQuota,
    quota?.used[variation.id]
  );
}

/**
 * Adakah varian yang bisa dipesan dan memuat `optionId` SEKALIGUS semua
 * `otherSelectedOptionIds` (pilihan yang sedang aktif di grup lain)?
 *
 * Bentuk umum `hasInStockVariationForOption` yang juga sadar KUOTA PO, jadi
 * satu fungsi menjawab pertanyaan yang sama untuk ready stock maupun PO
 * berkuota. `otherSelectedOptionIds` kosong = pertanyaan AGREGAT ("opsi ini
 * masih hidup di kombinasi mana pun"); terisi = pertanyaan SILANG ("opsi ini
 * hidup bersama pilihan yang sedang terlihat di layar").
 */
export function hasEligibleVariationForOption(
  stockTracked: boolean,
  variations: ProductVariation[],
  optionId: string,
  otherSelectedOptionIds: string[] = [],
  quota?: PoQuotaContext
): boolean {
  return variations.some(
    (v) =>
      (v.optionIds ?? []).includes(optionId) &&
      otherSelectedOptionIds.every((oid) => (v.optionIds ?? []).includes(oid)) &&
      isVariationEligible(stockTracked, v, quota)
  );
}

/** Satu grup yang pilihannya ikut BERGESER akibat klik di grup lain. */
export type VariantShift = {
  groupName: string;
  /** Nama opsi sebelum klik. */
  from: string;
  /** Nama opsi sesudah klik. */
  to: string;
};

/**
 * Grup MANA yang ikut berganti pilihan karena pembeli mengetuk opsi di grup
 * lain — bahan kalimat "Warna disesuaikan dari Merah ke Biru".
 *
 * KENAPA PERLU DIKATAKAN. `VariantGroupSelector` sengaja tidak memaksa
 * kombinasi persis: mengetuk sebuah opsi memilih varian TERBAIK yang memuat
 * opsi itu, sehingga mengklik Warna yang ukurannya habis tetap mendarat di
 * ukuran yang masih terbuka alih-alih kombinasi mati. Itu ramah — tapi
 * pergeserannya selama ini TANPA SUARA, jadi pembeli yang mengetuk "XS" lalu
 * langsung menekan Tambah ke Keranjang bisa memesan warna yang tidak ia pilih.
 *
 * Grup yang DIKETUK tidak pernah dilaporkan: ia memang berubah atas kehendak
 * pembeli. Grup yang tidak punya pilihan sebelum/sesudah juga dilewati —
 * tak ada yang "bergeser" dari ketiadaan.
 */
export function describeVariantShift(
  groups: VariantGroup[],
  before: string[],
  after: string[],
  clickedGroupId: string
): VariantShift[] {
  const out: VariantShift[] = [];
  for (const group of groups) {
    if (group.id === clickedGroupId) continue;
    const from = group.options.find((o) => before.includes(o.id));
    const to = group.options.find((o) => after.includes(o.id));
    if (!from || !to || from.id === to.id) continue;
    out.push({ groupName: group.name, from: from.name, to: to.name });
  }
  return out;
}

/**
 * Apakah sebuah OPSI varian (tombol Warna/Ukuran di `VariantGroupSelector`) boleh
 * dipilih pembeli. Sumber tunggal keputusan enable/disable tombol variasi, supaya
 * aturan "preorder tidak pernah dikunci oleh stok" tak bisa lepas dari salah satu
 * tempat render.
 *
 * Aturan:
 *  - Tak ada varian yang memuat opsi ini → tak bisa dipilih (bukan kombinasi valid).
 *  - Produk **preorder** (`stockTracked=false`) → **SELALU** bisa dipilih walau stok
 *    varian 0. Preorder diproduksi mengikuti pesanan; stok 0 itu normal dan TIDAK
 *    boleh mengunci tombol variasi (mencegah "tombol variasi tidak bisa diklik").
 *  - Produk **ready stock** (`stockTracked=true`) → bisa dipilih hanya bila ADA
 *    varian in-stock yang memuat opsi ini (grey-out ala Shopee saat habis).
 */
export function isVariantOptionSelectable(
  stockTracked: boolean,
  variations: ProductVariation[],
  optionId: string,
  quota?: PoQuotaContext
): boolean {
  const hasVariation = variations.some((v) =>
    (v.optionIds ?? []).includes(optionId)
  );
  if (!hasVariation) return false;
  if (stockTracked) {
    return hasEligibleVariationForOption(true, variations, optionId);
  }
  // Preorder BERKUOTA (mig 3970): jatahnya yang mengunci, bukan stok — opsi
  // hidup selama ADA kombinasi memuatnya yang jatahnya belum habis.
  if (quota?.limited) {
    return hasEligibleVariationForOption(false, variations, optionId, [], quota);
  }
  // Preorder biasa: stok tidak ditrack → opsi tak pernah dikunci oleh stok.
  return true;
}

/**
 * Pcs yang sudah dipesan per varian, hasil RPC `get_preorder_quota_remaining`.
 * SENGAJA dipisah dari `ProductVariation`: angkanya datang dari permintaan lain
 * (dan bergerak tanpa produknya berubah), jadi menempelkannya ke baris varian
 * akan membuat data yang sudah basi terlihat sama segarnya dengan harga.
 */
export type PoQuotaContext = {
  limited: boolean;
  used: Record<string, number>;
};

/**
 * Apakah varian ini masih bisa dipesan — SATU sumbu untuk kedua tipe produk.
 *
 * - ready stock → stok fisik yang menentukan
 * - preorder berkuota → sisa jatah yang menentukan
 * - preorder biasa → selalu bisa (barangnya diproduksi mengikuti pesanan)
 */
export function isVariationAvailable(
  product: Pick<Product, "productType" | "poLimited"> | undefined | null,
  variation: Pick<ProductVariation, "id" | "stockQty" | "poQuota"> | undefined | null,
  quotaUsed?: Record<string, number>
): boolean {
  if (isStockTracked(product)) return isVariationInStock(variation);
  if (!product?.poLimited) return true;
  if (!variation) return true;
  return !isQuotaExhausted(true, variation.poQuota, quotaUsed?.[variation.id]);
}

export type ProductVariation = {
  id: string;
  name: string;
  sku: string;
  /** UUID option yang dipilih per grup, sesuai variant_groups/variant_options */
  optionIds?: string[];
  price_normal?: number;
  /** Harga TAMBAHAN per varian (mig 7900) — ditempel SESUDAH seluruh potongan
   *  (level/override/merek/promo/voucher/diskon manual); `price_normal` tetap
   *  DASAR yang dipotong. Absen / 0 = tanpa tambahan. Rumusnya satu:
   *  `explainVariantPrice` (pricing.ts). */
  price_extra?: number;
  price_reseller?: number;
  price_agen?: number;
  stockQty?: number;
  /** Batas pcs PO untuk varian ini (product_variants.po_quota, mig 3970).
   *  Hanya bermakna saat `Product.poLimited`. */
  poQuota?: number;
  weightGram?: number;
  /** Override diskon per-varian per grup (mig 5600: 8 slot; mig 7300: 15 slot).
   *  Mapping slot → field ada di `DISKON_SLOT_KEY` (reseller-groups.ts) —
   *  jangan menuliskannya ulang di tempat lain. */
  diskonMode?: "persen" | "nominal";
  diskonReseller?: number;
  diskonAgen?: number;
  diskonSlot3?: number;
  diskonSlot4?: number;
  diskonSlot5?: number;
  diskonSlot6?: number;
  diskonSlot7?: number;
  diskonSlot8?: number;
  diskonSlot9?: number;
  diskonSlot10?: number;
  diskonSlot11?: number;
  diskonSlot12?: number;
  diskonSlot13?: number;
  diskonSlot14?: number;
  diskonSlot15?: number;
};

export type VariantOption = {
  id: string;
  name: string;
  imageUrl?: string;
  sortOrder: number;
};

export type VariantGroup = {
  id: string;
  name: string;
  sortOrder: number;
  useAsImagePrimary: boolean;
  options: VariantOption[];
};

export type Product = {
  id: string;
  slug: string;
  name: string;
  brand: string;
  /**
   * Link Telegram MEREK produk ini (`product_brands.telegram_url`, mig 3440),
   * sudah dalam bentuk kanonik `https://t.me/…`. Diisi admin di
   * `/admin/brands/[id]`; `undefined` = merek belum punya link, dan halaman
   * detail produk MENYEMBUNYIKAN tombol "Materi iklan"-nya.
   *
   * Bukan kolom `products`: merek diresolusi BY NAMA (`products.brand` cuma
   * snapshot teks, bukan FK) — lihat `getPublicBrandBadge`.
   */
  brandTelegramUrl?: string;
  /**
   * Logo MEREK produk ini (`product_brands.icon_url`, mig 096), sudah lolos
   * `normalizeBrandIconUrl`. `undefined` = merek belum punya logo (atau nilai
   * tersimpannya bukan URL) → badge merek di halaman detail produk memakai
   * ikon Tag seperti sebelumnya. Diresolusi BY NAMA, sama seperti
   * `brandTelegramUrl`.
   */
  brandIconUrl?: string;
  category?: string;
  /** Segmen produk (products.segment, mig 159) — atribut "untuk siapa" (Anak/Dewasa), sumbu terpisah dari kategori. */
  segment?: string;
  deadline: string;
  estimatedReady: string;
  status: PreorderStatus;
  /** Tipe produk (products.product_type). Menentukan apakah stok ditrack per-varian. */
  productType?: "preorder" | "ready_stock";
  /**
   * PO berkuota? (products.po_limited, mig 3970). Toggle inilah yang menentukan
   * apakah `ProductVariation.poQuota` berlaku — angka yang tersimpan saat toggle
   * mati SENGAJA tidak dihapus (gelombang PO berikutnya memakai angka yang sama).
   */
  poLimited?: boolean;
  price_normal?: number;
  price_reseller?: number;
  price_agen?: number;
  /**
   * HARGA PERKIRAAN (`products.price_estimate`, mig 6840) — angka PAJANGAN untuk
   * PO "harga menyusul": halaman produk ("Perkiraan Rp X") & feed Katalog
   * Instagram. Selalu dibaca lewat `effectivePriceEstimate`
   * (`src/lib/priceEstimate.ts`), yang mengabaikannya begitu ada satu harga asli.
   * JALUR UANG TAK PERNAH MEMBACANYA (pricing.ts, checkout, nota, DP) — dikunci
   * `priceEstimateLint.test.ts`. Hanya diisi jalur DETAIL & feed; listing tidak.
   */
  priceEstimate?: number;
  stockQty?: number;
  weightGram?: number;
  description: string;
  resellerDiscount?: string;
  agentDiscount?: string;
  /** PO wajib uang muka? (products.dp_required) */
  dpRequired?: boolean;
  /** Nilai DP — maknanya tergantung dpMode (products.dp_amount) */
  dpAmount?: number;
  /** Mode DP: 'order' Rp/order (lama) · 'per_pcs' Rp/pcs · 'percent' % harga (mig 070) */
  dpMode?: "order" | "per_pcs" | "percent";
  images: string[];
  /**
   * Foto PANDUAN UKURAN (mig 5890) — foto yang ditunjuk admin di tab Foto,
   * ditampilkan di popup "Panduan Ukuran" halaman produk. Kosong/undefined =
   * produk ini belum menunjuk apa pun, dan tombolnya TIDAK dirender.
   *
   * Foto ini juga TETAP ada di `images`: ia dipilih DARI foto produk, jadi
   * membuangnya dari galeri berarti menghapus foto yang sengaja diunggah admin.
   * Popup cuma jalan pintas ke foto yang sudah tayang.
   *
   * Hanya diisi jalur DETAIL produk (`PRODUCT_DETAIL_SELECT`) — listing beranda
   * sengaja tak mengambil kolomnya, kartu produk tak punya tombol ini.
   */
  sizeGuideImage?: string;
  variations: ProductVariation[];
  /** Grup variasi (Warna, Ukuran, dll) — kosong jika produk belum pakai sistem grup */
  variantGroups: VariantGroup[];
  /**
   * MODE MALAS (lihat `src/lib/lazyVariants.ts`): `variations` SENGAJA kosong
   * karena produk ini punya terlalu banyak kombinasi untuk dikirim; picker
   * dirender dari `variantGroups` dan kombinasi terpilih diresolusi on-demand.
   *
   * PENTING bagi pembaca `variations`: di mode ini array kosong TIDAK berarti
   * "produk tanpa varian" — ia berarti "belum dimuat". Jangan menyimpulkan
   * ketiadaan varian dari panjang array tanpa memeriksa flag ini.
   */
  variantsLazy?: boolean;
  /**
   * Rentang `price_normal` varian — HANYA diisi di mode malas, tempat
   * `variations` sengaja dikosongkan. Tanpa ini halaman produk tak bisa
   * membedakan produk ber-harga TUNGGAL dari produk ber-harga CAMPURAN, dan
   * akan memajang `products.price_normal` (MIN seluruh varian — angka "mulai
   * dari") sebagai harga varian yang sedang dipilih pembeli.
   *
   * Sejak Fase 0 (mig 5450) diturunkan dari AGREGAT SQL
   * (`product_variant_price_range`), bukan dari cuplikan baris yang sempat
   * terambil — cuplikan itu bisa melaporkan "seragam" untuk produk yang
   * sebenarnya campuran. Gerbang yang mengikat tetap di resolver checkout yang
   * memegang daftar penuh. `null` = produk ini memang belum punya harga varian
   * (keadaan SAH); untuk "tak diketahui" lihat `variantPriceUncertain`.
   * `minBase` = MIN DASAR (`min_price`) — bahan uji kesepakatan tambahan
   * (bughunt D14-2, `VariantPriceSummary` di pricing.ts).
   */
  variantPriceRange?: { min: number; max: number; minBase?: number; maxBase?: number } | null;
  /**
   * Rentang di atas GAGAL diketahui (agregat tak terjawab), berbeda dari
   * "produk ini memang belum punya harga varian" (`variantPriceRange: null`).
   * Dipakai `resolveSelectionPrice` untuk FAIL-CLOSED: ketidaktahuan
   * diperlakukan seperti harga campuran, sehingga varian tanpa harga sendiri
   * jatuh ke "harga menyusul" alih-alih meminjam harga varian lain.
   */
  variantPriceUncertain?: boolean;
  /**
   * SUMBU HARGA (2026-10-20, mig 7680): produk menyatakan grup penentu harga
   * (`products.price_affecting_groups` tak kosong) — dibaca lewat RPC
   * `product_has_price_axis` karena kolomnya sengaja tak terbaca anon (daftar
   * sensitif mig 4280). `true` → varian tanpa harga sendiri hanya boleh
   * berharga dari warisan slot, tak pernah dari keseragaman varian lain
   * (Mukena 17 Sep: NAJWA ditagih harga RAJWA). `false` → aturan lama.
   * `null` = tak diketahui (RPC gagal) → `resolveSelectionPrice` fail-closed.
   * Absen = pemanggil tak memuatnya (aturan lama).
   */
  priceAxis?: boolean | null;
  /**
   * Kombinasi yang DIMATIKAN admin (`product_variants.is_active = false`),
   * sebagai daftar `option_ids` — HANYA diisi di mode malas (mig 5830; sejak
   * mig 6300 selalu UTUH, lihat `parseBlockedComboList`).
   *
   * Kenapa harus dikirim terpisah, bukan disimpulkan dari `variations`: policy
   * RLS pembeli menuntut `is_active = true`, jadi baris yang dimatikan tidak
   * pernah ikut terbaca. Bagi pembeli ia tampak PERSIS SAMA dengan kombinasi
   * yang barisnya belum bermateri — yang justru alur normal mode malas. Tanpa
   * daftar ini tombol variasinya tetap bisa diketuk dan penolakannya baru
   * muncul di checkout (bug live natasyashop, lihat `lazyVariants.ts`).
   *
   * Kosong = tak ada yang dikunci (juga saat daftarnya gagal dimuat); gerbang
   * yang mengikat ada di RPC `resolve_or_create_preorder_variant`.
   */
  blockedOptionCombos?: string[][];
  /**
   * Mode daftar kombinasi (B1, `src/lib/comboListMode.ts`):
   * `products.absent_combos_sellable`. `false` = TERTUTUP (kombinasi tanpa baris
   * tak dijual), `true` = TERBUKA, `null`/absen = aturan lama (dihitung dari
   * jumlah kombinasi). Hanya hidup di toko yang saklarnya menyala.
   */
  absentCombosSellable?: boolean | null;
  /**
   * Daftar-IZIN kombinasi yang DIJUAL (`option_ids` baris aktif) — HANYA diisi
   * untuk produk TERTUTUP di mode malas (RPC `preorder_sellable_combo_list`).
   * Terisi = pemilih mengunci setiap kombinasi di luar daftar
   * (`pickerBlockedSet`); absen = daftar mati biasa (`blockedOptionCombos`).
   */
  sellableOptionCombos?: string[][];
};

export const products: Product[] = [
  {
    id: "sample-luna-tote",
    slug: "luna-soft-tote",
    name: "Luna Soft Tote",
    brand: "Studio Sample",
    category: "Tas",
    deadline: "29 Mei 2026",
    estimatedReady: "12 Juni 2026",
    status: "OPEN PO",
    price_normal: 189000,
    description:
      "Tote ringan dengan bentuk lembut untuk daily look. Cocok untuk kerja, kuliah, dan jalan santai.",
    resellerDiscount: "10% mulai 3 pcs",
    agentDiscount: "15% mulai 12 pcs",
    images: [
      "https://images.unsplash.com/photo-1590874103328-eac38a683ce7?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1584917865442-de89df76afd3?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1622560480605-d83c853bc5c3?auto=format&fit=crop&w=900&q=80"
    ],
    variations: [
      { id: "cream", name: "Cream", sku: "LUNA-CREAM" },
      { id: "black", name: "Black", sku: "LUNA-BLACK" },
      { id: "mocha", name: "Mocha", sku: "LUNA-MOCHA" }
    ],
    variantGroups: []
  },
  {
    id: "sample-nara-sling",
    slug: "nara-sling-bag",
    name: "Nara Sling Bag",
    brand: "Studio Sample",
    category: "Tas",
    deadline: "31 Mei 2026",
    estimatedReady: "15 Juni 2026",
    status: "READY SEBAGIAN",
    price_normal: 159000,
    description:
      "Sling bag compact dengan strap adjustable dan ruang utama yang cukup untuk kebutuhan harian.",
    resellerDiscount: "8% mulai 3 pcs",
    agentDiscount: "14% mulai 10 pcs",
    images: [
      "https://images.unsplash.com/photo-1575032617751-6ddec2089882?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1606522754091-a3bbf9ad4cb3?auto=format&fit=crop&w=900&q=80"
    ],
    variations: [
      { id: "taupe", name: "Taupe", sku: "NARA-TAUPE" },
      { id: "rose", name: "Dusty Rose", sku: "NARA-ROSE" }
    ],
    variantGroups: []
  },
  {
    id: "sample-mika-dress",
    slug: "mika-flow-dress",
    name: "Mika Flow Dress",
    brand: "Maison Sample",
    category: "Dress",
    deadline: "2 Juni 2026",
    estimatedReady: "18 Juni 2026",
    status: "PROSES INPUT",
    description:
      "Dress jatuh dengan bahan flowy, nyaman untuk acara semi-formal dan styling harian.",
    resellerDiscount: "Harga reseller menyusul",
    agentDiscount: "Harga agen menyusul",
    images: [
      "https://images.unsplash.com/photo-1539008835657-9e8e9680c956?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1529139574466-a303027c1d8b?auto=format&fit=crop&w=900&q=80"
    ],
    variations: [
      { id: "s", name: "S", sku: "MIKA-S" },
      { id: "m", name: "M", sku: "MIKA-M" },
      { id: "l", name: "L", sku: "MIKA-L" },
      { id: "xl", name: "XL", sku: "MIKA-XL" }
    ],
    variantGroups: []
  },
  {
    id: "sample-aria-blouse",
    slug: "aria-puff-blouse",
    name: "Aria Puff Blouse",
    brand: "Maison Sample",
    category: "Blouse",
    deadline: "25 Mei 2026",
    estimatedReady: "10 Juni 2026",
    status: "CLOSED PO",
    price_normal: 139000,
    description:
      "Blouse puff sleeve dengan potongan clean. Mudah dipadukan untuk office look atau casual brunch.",
    resellerDiscount: "9% mulai 4 pcs",
    agentDiscount: "16% mulai 15 pcs",
    images: [
      "https://images.unsplash.com/photo-1485968579580-b6d095142e6e?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=900&q=80"
    ],
    variations: [
      { id: "ivory", name: "Ivory", sku: "ARIA-IVORY" },
      { id: "sage", name: "Sage", sku: "ARIA-SAGE" },
      { id: "navy", name: "Navy", sku: "ARIA-NAVY" }
    ],
    variantGroups: []
  },
  {
    id: "sample-vela-mini",
    slug: "vela-mini-bag",
    name: "Vela Mini Bag",
    brand: "Studio Sample",
    category: "Tas",
    deadline: "5 Juni 2026",
    estimatedReady: "22 Juni 2026",
    status: "PROSES PRODUKSI",
    price_normal: 149000,
    description:
      "Mini bag structured dengan handle pendek dan strap panjang yang bisa dilepas.",
    resellerDiscount: "10% mulai 3 pcs",
    agentDiscount: "15% mulai 12 pcs",
    images: [
      "https://images.unsplash.com/photo-1614179689702-355944cd0918?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1548036328-c9fa89d128fa?auto=format&fit=crop&w=900&q=80"
    ],
    variations: [
      { id: "caramel", name: "Caramel", sku: "VELA-CARAMEL" },
      { id: "onyx", name: "Onyx", sku: "VELA-ONYX" }
    ],
    variantGroups: []
  },
  {
    id: "sample-senna-cardigan",
    slug: "senna-knit-cardigan",
    name: "Senna Knit Cardigan",
    brand: "Maison Sample",
    category: "Outer",
    deadline: "Ready",
    estimatedReady: "Ready lengkap",
    status: "READY LENGKAP",
    price_normal: 179000,
    description:
      "Cardigan rajut halus dengan fit santai. Cocok untuk layering ringan.",
    resellerDiscount: "7% mulai 3 pcs",
    agentDiscount: "12% mulai 10 pcs",
    images: [
      "https://images.unsplash.com/photo-1434389677669-e08b4cac3105?auto=format&fit=crop&w=900&q=80",
      "https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?auto=format&fit=crop&w=900&q=80"
    ],
    variations: [
      { id: "milk", name: "Milk", sku: "SENNA-MILK" },
      { id: "ash", name: "Ash", sku: "SENNA-ASH" }
    ],
    variantGroups: []
  }
];

export { formatPrice } from "@/lib/pricing";

export function getStatusTone(status: PreorderStatus | string) {
  switch (status) {
    case "OPEN PO":
      return "bg-leaf text-white";
    case "CLOSED PO":
      return "bg-zinc-800 text-white";
    case "PROSES INPUT":
      return "bg-marigold text-white";
    case "PROSES PRODUKSI":
    case "PRODUKSI":
      return "bg-shopee text-white";
    case "BELUM READY":
    case "Belum Ready":
      return "bg-zinc-500 text-white";
    case "READY TOKO":
    case "READY LENGKAP":
    case "READY":
      return "bg-sky-700 text-white";
    case "READY SEBAGIAN":
      // Warna STATUS (bukan brand): harus tetap berbeda dari "PROSES PRODUKSI"
      // yang memang memakai warna tema (bg-shopee). Karena itu TIDAK ikut tema.
      return "bg-pink-600 text-white";
    case "PENDISTRIBUSIAN":
    case "TERKIRIM":
      return "bg-indigo-700 text-white";
    case "GAGAL":
      // Status terminal gagal produksi — merah pekat, sengaja TIDAK ikut tema
      // toko: ini peringatan, bukan warna brand.
      return "bg-red-700 text-white";
    case "Menunggu":
      return "bg-zinc-100 text-zinc-700";
    case "Diproses":
      return "bg-marigold text-white";
    case "Produksi":
      return "bg-shopee text-white";
    case "Ready":
      return "bg-leaf text-white";
    case "Dikirim":
      return "bg-sky-700 text-white";
    default:
      return "bg-zinc-800 text-white";
  }
}
